import {
  normalizeMaterialOptions, normalizeMonthlyMaterials, monthlyMaterialsToDraft,
  monthlyMaterialSignature, getMonthlyMaterialState, monthlyMaterialReceiptMatches,
  hasCompleteManualMaterialChoices, savedMaterialsToDraft, materialSelectionsForRows,
} from "./rsoMaterialForecastHelpers";
import {validHistorySnapshot} from "./rsoHistoryHelpers";

const MAX_FORECAST_UNITS = 2147483647;

const firstDefined = (...values) =>
  values.find((value) => value !== undefined && value !== null) ?? null;

const asArray = (value) => (Array.isArray(value) ? value : []);
const isRecord = (value) => value !== null && typeof value === "object" && !Array.isArray(value);
const isForecastMonth = (value) => typeof value === "string" && /^(?!0000)\d{4}-(0[1-9]|1[0-2])-01$/.test(value);

const asText = (value) => {
  if (value === undefined || value === null) return "";
  const text = String(value).trim();
  return /^(null|undefined)$/i.test(text) ? "" : text;
};

export const currentForecastMonth = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
};

export const toForecastMonthDate = (month) =>
  isForecastMonth(`${month}-01`) ? `${month}-01` : "";

export const forecastUnitsToInput = (value) =>
  value === undefined || value === null || value === "" ? "" : String(value);

export const parseForecastUnits = (rawValue) => {
  const raw = String(rawValue ?? "").trim();
  if (raw === "") return { valid: true, answered: false, value: null };
  if (!/^\d+$/.test(raw)) {
    return { valid: false, answered: true, value: null };
  }

  const value = Number(raw);
  if (!Number.isSafeInteger(value) || value > MAX_FORECAST_UNITS) {
    return { valid: false, answered: true, value: null };
  }

  return { valid: true, answered: true, value };
};

const normalizeMaterial = (material = {}) => {
  const privateName = asText(material.privateName);
  return {
    productId: firstDefined(material.productId, null),
    productStructure: asText(material.productStructure),
    productStore: asText(material.productStore),
    privateName,
    // Private Name is the customer-facing identity when supplied. Mask the
    // branded fields even if the upstream payload also happens to populate them.
    manufacturer: privateName ? "" : asText(material.manufacturer),
    style: privateName ? "" : asText(material.style),
    color: privateName ? "" : asText(material.color),
    quantity: firstDefined(material.quantity, ""),
    uom: asText(material.uom),
    isRecurringStock: typeof material.isRecurringStock === "boolean" ? material.isRecurringStock : null,
  };
};

const validId = (value) => /^\d+$/.test(String(value ?? "")) &&
  Number.isSafeInteger(Number(value)) && Number(value) > 0 && Number(value) <= MAX_FORECAST_UNITS;
const idKey = (value) => validId(value) ? String(Number(value)) : String(value);
const validLayoutIds = (layouts) => layouts.length > 0 &&
  layouts.every((layout) => validId(layout.unitTypeId)) &&
  new Set(layouts.map((layout) => idKey(layout.unitTypeId))).size === layouts.length;

const labelLayouts = (layouts) => {
  const names = layouts.map((layout) => {
    const unitType = asText(layout.unitType);
    const defaultFloorplanName = asText(layout.defaultFloorplanName) || asText(layout.floorplanName);
    return { unitType, defaultFloorplanName, name: unitType || defaultFloorplanName || "Unnamed floorplan" };
  });
  const frequency = (values) => values.reduce((counts, value) => {
    const key = value.toLowerCase();
    counts.set(key, (counts.get(key) || 0) + 1);
    return counts;
  }, new Map());
  const repeatedTypes = frequency(names.map((item) => item.name));
  const detailedNames = names.map(({ name, unitType, defaultFloorplanName }) =>
    repeatedTypes.get(name.toLowerCase()) > 1 && unitType && defaultFloorplanName &&
      unitType.toLowerCase() !== defaultFloorplanName.toLowerCase()
      ? `${name} · ${defaultFloorplanName}` : name);
  const repeatedNames = frequency(detailedNames);
  return layouts.map((layout, index) => ({
    ...layout,
    unitType: names[index].unitType,
    defaultFloorplanName: names[index].defaultFloorplanName,
    // Apartment type is the name Sales recognizes. A flooring-layout code such
    // as VT-1 is not an apartment type and can be shared by many apartment types.
    floorplanName: repeatedNames.get(detailedNames[index].toLowerCase()) > 1
      ? `${detailedNames[index]} (ID ${idKey(layout.unitTypeId)})` : detailedNames[index],
  }));
};

const normalizeLayout = (layout = {}, propertyEditable = false, soleLayoutUnits) => ({
  unitTypeId: firstDefined(layout.unitTypeId, null),
  unitType: asText(layout.unitType),
  defaultFloorplanName: asText(layout.defaultFloorplanName) || asText(layout.floorplanName),
  floorplanName: asText(layout.unitType) || asText(layout.floorplanName) || "Unnamed floorplan",
  // An old single-layout response had only the property total. Never use a
  // total to invent a split, or replace an explicitly unanswered layout.
  forecastUnits: layout.forecastUnits === undefined
    ? firstDefined(soleLayoutUnits, null) : firstDefined(layout.forecastUnits, null),
  isEditable: layout.isEditable === undefined ? propertyEditable : layout.isEditable === true,
  submissionStatus: asText(layout.submissionStatus),
  setupMessage: asText(layout.setupMessage),
  materials: asArray(layout.materials).map(normalizeMaterial),
});

export const normalizeForecastProperty = (property = {}, materialOptions = property.materialOptions) => {
  if (property.forecastRevision != null && (!Number.isSafeInteger(property.forecastRevision) || property.forecastRevision < 0)) {
    throw new Error("The saved forecast version is incomplete. Reload before editing.");
  }
  if (property.savedSnapshot != null && !validHistorySnapshot(property.savedSnapshot)) {
    throw new Error("The saved forecast details are incomplete. Reload before editing.");
  }
  const rawLayouts = asArray(property.layouts);
  const isManualException = property.isManualException === true;
  const rawScope = property.rsoScope === undefined ? "" : asText(property.rsoScope).toLowerCase();
  // Sales sees only positively confirmed RSO properties. Missing or unknown
  // scope fails closed; legacy permission alone cannot expose a property.
  const rsoScope = asText(property.submissionStatus).toLowerCase() === "not_applicable" ? "not_applicable"
    : ["rso", "not_applicable", "unconfirmed"].includes(rawScope) ? rawScope : "unconfirmed";
  const layouts = labelLayouts(rawLayouts.map((layout) => normalizeLayout(layout, property.isEditable === true,
    rawLayouts.length === 1 ? property.forecastUnits : undefined)));
  return {
    contactInfoId: firstDefined(property.contactInfoId, null),
    costarId: asText(property.costarId),
    propertyName: asText(property.propertyName) || "Unnamed property",
    store: asText(property.store),
    storeId: validId(property.storeId) ? Number(property.storeId) : null,
    materialOptions: normalizeMaterialOptions(materialOptions).filter((option) => option.storeId === Number(property.storeId)),
    materialForecasts: normalizeMonthlyMaterials(property.materialForecasts),
    ...(Number.isSafeInteger(property.forecastRevision) && property.forecastRevision >= 0 ? {forecastRevision:property.forecastRevision} : {}),
    materialSelections: Array.isArray(property.materialSelections) ? property.materialSelections.map((row) => ({
      sourceProductId:row.sourceProductId,productId:row.productId,
    })) : null,
    savedSnapshot: isRecord(property.savedSnapshot) ? property.savedSnapshot : null,
    forecastUnits: firstDefined(property.forecastUnits, null),
    rsoScope,
    isManualException,
    excludedNonRsoLayoutCount: Number.isSafeInteger(property.excludedNonRsoLayoutCount) && property.excludedNonRsoLayoutCount >= 0
      ? property.excludedNonRsoLayoutCount : 0,
    // Permission must be positively confirmed. Missing/duplicate floorplan IDs
    // or any unavailable floorplan cannot become a partially editable property.
    isEditable: property.isEditable === true && (rsoScope === "rso" || (isManualException && rsoScope === "not_applicable")) && validId(property.contactInfoId) &&
      validLayoutIds(layouts) && layouts.every((layout) => layout.isEditable),
    submittedOn: firstDefined(property.submittedOn, null),
    submissionStatus: asText(property.submissionStatus),
    setupMessage: asText(property.setupMessage),
    layouts,
  };
};

const responseMessage = (data) => {
  if (!data) return "";
  if (typeof data === "string") return data;
  if (typeof data.responseMessage === "string" && data.responseMessage !== "Success") {
    return data.responseMessage;
  }
  if (typeof data.message === "string") return data.message;
  if (typeof data.error === "string") return data.error;
  if (typeof data.error?.message === "string") return data.error.message;
  if (Array.isArray(data.result) && typeof data.result[0] === "string") {
    return data.result[0];
  }
  return "";
};

// NANCY wraps successful payloads in result[0] with a string "Success" status.
// A direct object is supported too, but an error or malformed response must
// never be normalized into an apparently successful empty portfolio.
const unwrapForecastResponse = (responseData, fallback) => {
  if (!isRecord(responseData)) throw new Error(fallback);
  const status = responseData.responsestatus;
  const code = responseData.responsecode;
  if ((status !== undefined && status !== true && status !== "Success") ||
      (code !== undefined && String(code) !== "200") ||
      (responseData.error !== undefined && responseData.error !== null && responseData.error !== "")) {
    const error = new Error(responseMessage(responseData) || fallback);
    error.response = { status: Number(code) >= 400 ? Number(code) : 0, data: responseData };
    throw error;
  }
  const result = responseData.result;
  if (result === undefined) return responseData;
  if (Array.isArray(result) && result.length === 1 && isRecord(result[0])) return result[0];
  if (isRecord(result)) return result;
  throw new Error(fallback);
};

export const normalizeForecastPayload = (responseData, expectedMonth) => {
  const fallback = "The forecast service returned an incomplete property list. Reload to try again; your unsaved entries have been kept.";
  const payload = unwrapForecastResponse(responseData, fallback);
  const { page, pageSize, totalCount } = payload;
  const totalPages = payload.totalPages === 0 && totalCount === 0 ? 1 : payload.totalPages;
  if (!isForecastMonth(payload.forecastMonth) ||
      (expectedMonth !== undefined && payload.forecastMonth !== expectedMonth) ||
      !Number.isInteger(page) || page < 1 ||
      !Number.isInteger(pageSize) || pageSize < 1 ||
      !Number.isInteger(totalCount) || totalCount < 0 ||
      !Number.isInteger(totalPages) || totalPages < 1 ||
      totalPages !== Math.max(1, Math.ceil(totalCount / pageSize)) || page > totalPages ||
      (payload.materialOptions != null && (!Array.isArray(payload.materialOptions) || payload.materialOptions.some((option) => !isRecord(option)))) ||
      !Array.isArray(payload.addableProperties) ||
      !Array.isArray(payload.properties) || [...payload.properties, ...payload.addableProperties].some((property) =>
        !isRecord(property) || (property.materialForecasts != null && (!Array.isArray(property.materialForecasts) || property.materialForecasts.some((material) => !isRecord(material)))) ||
        !Array.isArray(property.layouts) || property.layouts.some((layout) =>
          !isRecord(layout) || !Array.isArray(layout.materials) || layout.materials.some((material) => !isRecord(material))))) {
    throw new Error(fallback);
  }
  const materialOptions = normalizeMaterialOptions(payload.materialOptions);
  const properties = payload.properties.map((property) => {
    const normalized = normalizeForecastProperty(property,materialOptions);
    // Collection membership is authoritative: the API returns persisted
    // month exceptions in properties and unsaved choices only in addableProperties.
    return {...normalized,isPersistedManualException:normalized.isManualException === true};
  });
  const addableProperties = payload.addableProperties.map((property) => ({
      ...normalizeForecastProperty(property,materialOptions),
      // A candidate is not a saved material forecast. Its existing non-RSO rows
      // are calculation sources only; Sales must explicitly choose an RSO item.
      materialForecasts:null,
    }))
    .filter(hasCompleteManualMaterialChoices);

  return {
    forecastMonth: asText(payload.forecastMonth),
    salespersonName: asText(payload.salespersonName),
    page,
    pageSize,
    totalCount,
    totalPages,
    materialOptions,
    properties,
    addableProperties,
  };
};

// Only a complete, matching receipt can clear the user's draft. HTTP 200 by
// itself says nothing about whether the forecasting service accepted it.
export const assertForecastSaved = (responseData, { forecastMonth, entries }) => {
  const fallback = "The forecast service did not confirm every saved entry. Your changes have been kept. Retry the unchanged forecast.";
  const payload = unwrapForecastResponse(responseData, fallback);
  if (payload.accepted !== true || payload.forecastMonth !== forecastMonth ||
      !isForecastMonth(forecastMonth) || !Array.isArray(entries) || entries.length === 0 ||
      !Array.isArray(payload.properties) || payload.properties.length !== entries.length) {
    throw new Error(fallback);
  }
  const validBreakdown = (entry) => {
    if (!isRecord(entry) || !validId(entry.contactInfoId) ||
        !Number.isInteger(entry.forecastUnits) || entry.forecastUnits < 0 || entry.forecastUnits > MAX_FORECAST_UNITS ||
        !Array.isArray(entry.layoutForecasts) || entry.layoutForecasts.length === 0) return false;
    const ids = new Set();
    let total = 0;
    for (const layout of entry.layoutForecasts) {
      if (!isRecord(layout) || !validId(layout.unitTypeId) || ids.has(idKey(layout.unitTypeId)) ||
          !Number.isInteger(layout.forecastUnits) || layout.forecastUnits < 0 || layout.forecastUnits > MAX_FORECAST_UNITS) return false;
      ids.add(idKey(layout.unitTypeId));
      total += layout.forecastUnits;
      if (total > MAX_FORECAST_UNITS) return false;
    }
    return total === entry.forecastUnits && monthlyMaterialReceiptMatches(entry.materialForecasts,entry.materialForecasts);
  };
  if (entries.some((entry) => !validBreakdown(entry))) throw new Error(fallback);
  const expected = new Map(entries.map((entry) => [idKey(entry.contactInfoId), entry]));
  const confirmed = new Set();
  if (expected.size !== entries.length || payload.properties.some((property) => {
    if (!validBreakdown(property)) return true;
    const key = idKey(property.contactInfoId);
    const requested = expected.get(key);
    if (confirmed.has(key) || !requested || property.forecastUnits !== requested.forecastUnits ||
        property.layoutForecasts.length !== requested.layoutForecasts.length) return true;
    const layoutCounts = new Map(requested.layoutForecasts.map((layout) => [idKey(layout.unitTypeId), layout.forecastUnits]));
    if (property.layoutForecasts.some((layout) => layoutCounts.get(idKey(layout.unitTypeId)) !== layout.forecastUnits) ||
        !monthlyMaterialReceiptMatches(requested.materialForecasts,property.materialForecasts)) return true;
    if (requested.expectedRevision !== undefined && (!Number.isSafeInteger(property.forecastRevision) ||
        property.forecastRevision < requested.expectedRevision || !validHistorySnapshot(property.savedSnapshot) ||
        property.savedSnapshot.forecastUnits !== property.forecastUnits ||
        JSON.stringify(materialSelectionsForRows(requested.materialSelections)) !== JSON.stringify(materialSelectionsForRows(property.materialSelections)))) return true;
    confirmed.add(key);
    return false;
  })) throw new Error(fallback);
  return payload;
};

const layoutDefinitions = (property) => labelLayouts(asArray(property.layouts).map((layout) => ({
  unitTypeId: layout.unitTypeId,
  floorplanName: asText(layout.floorplanName) || "Unnamed floorplan",
  unitType: asText(layout.unitType),
  defaultFloorplanName: asText(layout.defaultFloorplanName) || asText(layout.floorplanName),
  isEditable: layout.isEditable === undefined ? property.isEditable === true : layout.isEditable === true,
})));

const initialLayoutValues = (property) => {
  const layouts = asArray(property.layouts);
  return Object.fromEntries(layouts.map((layout) => [idKey(layout.unitTypeId), forecastUnitsToInput(
    layout.forecastUnits === undefined && layouts.length === 1 ? property.forecastUnits : layout.forecastUnits
  )]));
};

const splitSignature = (layouts, values) => {
  const keys = Object.keys(values).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  // Retain the old single-floorplan signature for in-memory draft compatibility.
  if (layouts.length === 1 && keys.length === 1 && keys[0] === idKey(layouts[0].unitTypeId)) return String(values[keys[0]]);
  return JSON.stringify(keys.map((key) => [key, String(values[key])]));
};
const forecastSignature = (layouts,values,materials,revision) => materials == null ? splitSignature(layouts,values)
  : JSON.stringify({apartments:splitSignature(layouts,values),materials:monthlyMaterialSignature(materials),
    ...(revision !== undefined ? {selections:materialSelectionsForRows(materials)} : {})});

const sameLayoutIds = (left, right) => left.length === right.length &&
  left.map((layout) => idKey(layout.unitTypeId)).sort().join(",") ===
    right.map((layout) => idKey(layout.unitTypeId)).sort().join(",");

const newForecastEdit = (property) => {
  const layouts = layoutDefinitions(property);
  const layoutValues = initialLayoutValues(property);
  const materialForecasts = savedMaterialsToDraft(property);
  const value = forecastSignature(layouts, layoutValues, materialForecasts,property.forecastRevision);
  return {
    contactInfoId: property.contactInfoId,
    propertyName: property.propertyName,
    isEditable: property.isEditable === true,
    layouts,
    originalLayouts: layouts,
    layoutValues,
    originalLayoutValues: { ...layoutValues },
    materialForecasts,
    originalMaterialForecasts: monthlyMaterialsToDraft(materialForecasts),
    storeId: property.storeId,
    materialOptions: property.materialOptions || [],
    isManualException: property.isManualException === true,
    ...(property.forecastRevision !== undefined ? {expectedRevision:property.forecastRevision} : {}),
    value,
    originalValue: value,
    layoutDefinitionChanged: false,
  };
};

// Handles an in-memory draft made just before upgrading from the old one-box UI.
// Its total is only recoverable when the property still has one floorplan.
const upgradeForecastEdit = (existing, property) => {
  if (existing.expectedRevision === undefined && property.forecastRevision !== undefined) {
    existing = {...existing,expectedRevision:property.forecastRevision};
    if (isRecord(existing.layoutValues) && existing.materialForecasts !== undefined) {
      existing.value = forecastSignature(existing.layouts,existing.layoutValues,existing.materialForecasts,existing.expectedRevision);
      existing.originalValue = forecastSignature(existing.originalLayouts,existing.originalLayoutValues,existing.originalMaterialForecasts,existing.expectedRevision);
    }
  }
  if (isRecord(existing.layoutValues)) {
    if (existing.materialForecasts !== undefined) return existing;
    const materials = monthlyMaterialsToDraft(property.materialForecasts);
    return {...existing,materialForecasts:materials,originalMaterialForecasts:monthlyMaterialsToDraft(materials),
      storeId:property.storeId,materialOptions:property.materialOptions || [],
      value:forecastSignature(existing.layouts,existing.layoutValues,materials),
      originalValue:forecastSignature(existing.originalLayouts,existing.originalLayoutValues,materials)};
  }
  const incoming = newForecastEdit(property);
  if (incoming.layouts.length !== 1) return { ...incoming, value: existing.value,
    originalValue: existing.originalValue, layoutDefinitionChanged: true };
  const key = idKey(incoming.layouts[0].unitTypeId);
  return { ...incoming, layoutValues: { [key]: existing.value },
    originalLayoutValues: { [key]: existing.originalValue }, value:forecastSignature(incoming.layouts,{[key]:existing.value},incoming.materialForecasts),
    originalValue:forecastSignature(incoming.layouts,{[key]:existing.originalValue},incoming.originalMaterialForecasts) };
};

export const mergeForecastEdits = (previous, properties) => {
  const next = { ...previous };
  properties.forEach((property) => {
    if (property.contactInfoId === null) return;
    const key = idKey(property.contactInfoId);
    const existing = next[key];
    const existingIsDirty =
      existing && String(existing.value) !== String(existing.originalValue);

    if (existingIsDirty) {
      const preserved = upgradeForecastEdit(existing, property);
      const layouts = layoutDefinitions(property);
      next[key] = {
        ...preserved,
        propertyName: property.propertyName,
        isEditable: property.isEditable === true,
        storeId: property.storeId,
        materialOptions: property.materialOptions || [],
        isManualException: property.isManualException === true,
        layouts,
        layoutDefinitionChanged: preserved.layoutDefinitionChanged ||
          !sameLayoutIds(preserved.originalLayouts, layouts),
      };
      return;
    }

    next[key] = newForecastEdit(property);
  });
  return next;
};

export const withLayoutForecastEdit = (previous, property, unitTypeId, value) => {
  const key = idKey(property.contactInfoId);
  const existing = previous[key] ? upgradeForecastEdit(previous[key], property) : newForecastEdit(property);
  const layouts = layoutDefinitions(property);
  const layoutValues = { ...existing.layoutValues, [idKey(unitTypeId)]: forecastUnitsToInput(value) };
  return {
    ...previous,
    [key]: {
      ...existing,
      propertyName: property.propertyName,
      isEditable: property.isEditable === true,
      layouts,
      layoutValues,
      storeId:property.storeId,
      materialOptions:property.materialOptions || [],
      isManualException:property.isManualException === true,
      value: forecastSignature(layouts, layoutValues, existing.materialForecasts,existing.expectedRevision),
      layoutDefinitionChanged: existing.layoutDefinitionChanged ||
        !sameLayoutIds(existing.originalLayouts, layouts),
    },
  };
};

export const withMonthlyMaterialEdit = (previous,property,materials) => {
  const key = idKey(property.contactInfoId);
  const existing = previous[key] ? upgradeForecastEdit(previous[key],property) : newForecastEdit(property);
  const materialForecasts = monthlyMaterialsToDraft(materials);
  return {...previous,[key]:{...existing,materialForecasts,propertyName:property.propertyName,
    isEditable:property.isEditable === true,isManualException:property.isManualException === true,
    storeId:property.storeId,materialOptions:property.materialOptions || [],
    value:forecastSignature(existing.layouts,existing.layoutValues,materialForecasts,existing.expectedRevision)}};
};

// Older callers may still edit a one-floorplan property. A property total is
// deliberately not accepted for a multi-floorplan property.
export const withForecastEdit = (previous, property, value) =>
  asArray(property.layouts).length === 1
    ? withLayoutForecastEdit(previous, property, property.layouts[0].unitTypeId, value)
    : previous;

export const getLayoutForecastEditState = (property, edit) => {
  const definitions = layoutDefinitions(property);
  const current = edit ? upgradeForecastEdit(edit, property) : newForecastEdit(property);
  const values = current.layoutValues;
  const keys = definitions.map((layout) => idKey(layout.unitTypeId));
  const layoutIdsValid = validLayoutIds(definitions) && Object.keys(values).length === keys.length &&
    Object.keys(values).every((key) => keys.includes(key));
  const layouts = definitions.map((layout) => {
    const value = forecastUnitsToInput(values[idKey(layout.unitTypeId)]);
    const parsed = parseForecastUnits(value);
    // Preserve the raw draft and its dirtiness. Only the reviewed/submitted
    // breakdown turns genuine blanks into zero, and only dirty properties
    // become submission entries. Never apply this default to saved data.
    return { ...layout, value, parsed, forecastUnits: parsed.valid ? parsed.value ?? 0 : null,
      implicitZero: parsed.valid && !parsed.answered, invalid: !parsed.valid };
  });
  const total = layouts.reduce((sum, layout) => sum + (layout.parsed.value ?? 0), 0);
  const totalValid = Number.isSafeInteger(total) && total <= MAX_FORECAST_UNITS;
  const layoutDefinitionChanged = current.layoutDefinitionChanged ||
    !sameLayoutIds(current.originalLayouts, definitions);
  let errorMessage = "";
  if (layoutDefinitionChanged) errorMessage = "The floorplans changed while you were editing. Discard this property's draft and reload before entering its counts again.";
  else if (!layoutIdsValid || !validId(property.contactInfoId)) errorMessage = "The property or floorplan IDs are incomplete. Ask your support team to check the setup before saving.";
  else if (property.isEditable !== true || layouts.some((layout) => !layout.isEditable)) errorMessage = "This property has a floorplan that needs setup. Its forecast cannot be saved yet.";
  else if (!totalValid) errorMessage = "The total unit count is too large. Check the numbers for each unit type.";
  else if (layouts.some((layout) => !layout.parsed.valid)) errorMessage = "Use a whole unit count of zero or more for every unit type.";
  return {
    layouts,
    total: totalValid ? total : null,
    answeredCount: layouts.filter((layout) => layout.parsed.answered).length,
    complete: !errorMessage,
    invalid: !!errorMessage,
    dirty: String(current.value) !== String(current.originalValue),
    layoutIdsValid,
    layoutDefinitionChanged,
    errorMessage,
  };
};

export const forecastEditState = (edits,properties = []) => {
  const dirty = Object.values(edits || {}).filter(
    (entry) =>
      entry.isEditable &&
      String(entry.value) !== String(entry.originalValue)
  );
  const states = dirty.map((entry) => {
    const property = asArray(properties).find((item) => idKey(item.contactInfoId) === idKey(entry.contactInfoId)) || entry;
    const summary = getLayoutForecastEditState(property,entry);
    return {entry,summary,materials:getMonthlyMaterialState(property,entry.materialForecasts,summary)};
  });
  const validState = ({entry,summary,materials}) => summary.complete && materials.valid &&
    (entry.isManualException !== true || (materials.custom && Array.isArray(materials.materialForecasts) && materials.materialForecasts.length > 0));
  const invalid = states.filter((state) => !validState(state)).map(({ entry }) => entry);
  const entries = states
    .filter(validState)
    .map(({ entry, summary,materials }) => ({
      contactInfoId: Number(entry.contactInfoId),
      forecastUnits: summary.total,
      layoutForecasts: summary.layouts.map((layout) => ({ unitTypeId: Number(layout.unitTypeId), forecastUnits: layout.forecastUnits }))
        .sort((a, b) => a.unitTypeId - b.unitTypeId),
      ...(materials.custom || entry.originalMaterialForecasts != null ? {materialForecasts:materials.materialForecasts} : {}),
      ...(entry.isManualException === true ? {isManualException:true} : {}),
      ...(entry.expectedRevision !== undefined ? {expectedRevision:entry.expectedRevision,
        materialSelections:materials.custom ? materialSelectionsForRows(materials.rows) : null} : {}),
    }))
    .sort((a, b) => String(a.contactInfoId).localeCompare(String(b.contactInfoId)));

  return { dirty, invalid, entries };
};

export const describeForecastError = (error, fallback) => {
  const status = Number(error?.response?.status || 0);
  const message = responseMessage(error?.response?.data) || error?.message || fallback;
  const text = message.toLowerCase();

  if (status === 401) {
    return { kind: "auth", title: "Please sign in again", message: "Your NANCY session has expired. Sign in again before saving a forecast." };
  }
  if (error?.code === "ECONNABORTED" || error?.code === "ETIMEDOUT" || text.includes("timeout")) {
    return { kind: "downstream", title: "Forecast service took too long", message: "We could not confirm a response. Your unsaved entries are still here. Try again without changing them." };
  }
  if (text === "network error") {
    return { kind: "downstream", title: "Cannot reach NANCY", message: "Check your connection and try again. Your unsaved entries are still here." };
  }

  if (status === 403 || text.includes("permission") || text.includes("forbidden")) {
    return { kind: "permission", title: "RSO forecast permission required", message };
  }
  if (status === 409 && !text.includes("mapping") && !text.includes("mapped")) {
    return {kind:"conflict",title:"This forecast changed",message:"Your draft is safe. Review it against the latest saved forecast before saving again."};
  }
  if (
    status === 409 ||
    text.includes("mapping") ||
    text.includes("mapped") ||
    text.includes("salesperson")
  ) {
    return { kind: "mapping", title: "Salesperson mapping required", message };
  }
  if (
    text.includes("not configured") ||
    text.includes("configuration") ||
    text.includes("disabled") ||
    text.includes("blank url")
  ) {
    return { kind: "config", title: "RSO forecast is not configured", message };
  }
  if (status === 400 || text.includes("validation") || text.includes("invalid")) {
    return { kind: "validation", title: "Request needs attention", message };
  }
  if (
    status === 502 ||
    status === 503 ||
    status === 504 ||
    text.includes("downstream") ||
    text.includes("unavailable") ||
    text.includes("timed out")
  ) {
    return { kind: "downstream", title: "Forecast service unavailable", message };
  }
  return { kind: "general", title: "Unable to load RSO forecasts", message };
};

export const createIdempotencyKey = () => {
  if (globalThis.crypto && typeof globalThis.crypto.randomUUID === "function") {
    return globalThis.crypto.randomUUID();
  }

  // UUID v4 fallback for older browsers/test runners. It is an idempotency
  // token, not a secret; Math.random is sufficient when Web Crypto is absent.
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
    const random = Math.floor(Math.random() * 16);
    const value = char === "x" ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
};
